import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { FilterChip } from '../../components/design-system/common/FilterChip';
import { DemoReason, DemoSells, JourneyFields, SignupConfig, useSignupApi } from '../../common/hooks/use-signup-api.hook';
import { SignupErrorCode, errorKey, readSignupError } from './signup-errors';
import { BusyRange, dayBounds, firstDay, formatSlot, freeSlots, isWeekday, lastDay, localTimeZone, slotInstant, slotsFor } from './demo-slots';
import { readJourney } from './signup-journey';
import { Turnstile } from './Turnstile';
import { useHumanCheck } from './use-human-check';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const DEMO_SELLS: DemoSells[] = ['online', 'offline', 'both'];
export const DEMO_REASONS: DemoReason[] = ['offline_store', 'cant_install_now', 'want_walkthrough', 'other'];

export interface BookDemoPrefill {
    name?: string;
    email?: string;
    phone?: string;
    company?: string;
    website?: string;
}

export interface BookDemoDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /**
     * Signed in (the gate, the review and blocked screens): the token goes
     * along and the API links the request to the vendor. Signed out (the
     * website step): the human check, the honeypot and the start time do.
     */
    signedIn?: boolean;
    prefill?: BookDemoPrefill;
    /** `/signup/config` when the caller already has it; otherwise read on open. */
    config?: SignupConfig | null;
    /** The journey to send; the stored one when omitted. */
    journey?: JourneyFields;
    defaultReason?: DemoReason;
    /** Opened from a prospect's demo page: its slug goes with the request (see pages/common/book-demo). */
    demoSlug?: string;
    onSent?: () => void;
}

/**
 * "Book a demo" — for the prospect who sells only in a showroom, or cannot
 * add the code today (docs/plg-review.md). One dialog for every entry point.
 * After sending: "we'll be in touch", and a link to pick a time straight away
 * when a booking page is set up.
 */
export const BookDemoDialog = ({ open, onOpenChange, ...rest }: BookDemoDialogProps) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/*
              Opened over the auth layout (z-[9999]) and the activation gate (z-[60]):
              a dialog at the default z-50 would sit, invisible, behind them.
            */}
            <DialogContent
                overlayClassName="z-[10000]"
                className="z-[10000] max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-[520px]"
                data-testid="book-demo-dialog"
            >
                {open && <DemoForm {...rest} onClose={() => onOpenChange(false)} />}
            </DialogContent>
        </Dialog>
    );
};

type Errors = Partial<Record<'name' | 'email' | 'sells' | 'when', boolean>>;

const DemoForm = ({
    signedIn = false,
    prefill,
    config: givenConfig,
    journey,
    defaultReason = 'want_walkthrough',
    demoSlug,
    onSent,
    onClose,
}: Omit<BookDemoDialogProps, 'open' | 'onOpenChange'> & { onClose: () => void }) => {
    const { t, i18n } = useTranslation();
    const api = useSignupApi();
    const locale = (i18n?.language || 'en').slice(0, 2);

    // When the form opened: the server refuses one sent faster than a person could fill it.
    const [startedAt] = useState(() => Date.now());
    const [config, setConfig] = useState<SignupConfig | null | undefined>(givenConfig);
    // Read at the moment of success: the config may arrive while the request is out.
    const configRef = useRef(config);
    configRef.current = config;
    // Only a signed-out visitor needs the human check; `undefined` keeps it idle.
    const human = useHumanCheck(signedIn ? undefined : config);

    useEffect(() => {
        if (givenConfig !== undefined) return undefined;
        let live = true;
        Promise.resolve(api.config())
            .then((c) => live && setConfig(c || null))
            .catch(() => live && setConfig(null));
        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [name, setName] = useState(prefill?.name || '');
    const [email, setEmail] = useState(prefill?.email || '');
    const [phone, setPhone] = useState(prefill?.phone || '');
    const [company, setCompany] = useState(prefill?.company || '');
    const [website, setWebsite] = useState(prefill?.website || '');
    const [sells, setSells] = useState<DemoSells | null>(null);
    const [reason, setReason] = useState<DemoReason>(defaultReason);
    const [day, setDay] = useState('');
    const [time, setTime] = useState('');
    const zone = useMemo(() => localTimeZone(), []);
    /*
     * Times already booked (ARDE-288), read from the API when the day changes
     * and again each time the time list opens, so a slot taken meanwhile is
     * not offered. If the API cannot be reached the day's slots all show: the
     * server checks the time again when the request is sent.
     */
    const [booked, setBooked] = useState<BusyRange[]>([]);
    const dayRef = useRef(day);
    dayRef.current = day;
    const refreshBusy = async (forDay: string = dayRef.current) => {
        const bounds = forDay ? dayBounds(forDay) : null;
        if (!bounds) return;
        try {
            const res = await api.demoBusy(bounds.from, bounds.to);
            if (dayRef.current === forDay) setBooked(res?.busy || []);
        } catch {
            // Offer the day as it is; sending will say if a time was taken.
        }
    };
    useEffect(() => {
        setBooked([]);
        if (day) refreshBusy(day);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [day]);
    const slots = useMemo(() => (day ? freeSlots(day, booked) : []), [day, booked]);
    // A weekday with times of its own, every one of them booked.
    const dayFull = !!day && isWeekday(day) && slotsFor(day).length > 0 && slots.length === 0;
    // The time picked was booked by someone else meanwhile: pick again.
    useEffect(() => {
        if (time && !slots.includes(time)) setTime('');
    }, [slots, time]);
    const [message, setMessage] = useState('');
    // The honeypot. Nobody sees it; a bot filling every field fills this too.
    const [companyUrl, setCompanyUrl] = useState('');

    const [errors, setErrors] = useState<Errors>({});
    const [busy, setBusy] = useState(false);
    const [failure, setFailure] = useState<SignupErrorCode | null>(null);
    const [sent, setSent] = useState<{ bookingUrl: string | null; when: string; email: string } | null>(null);

    const submit = async () => {
        const at = day && time ? slotInstant(day, time) : null;
        const next: Errors = { name: !name.trim(), email: !EMAIL.test(email.trim()), sells: !sells, when: !at || !slots.includes(time) };
        setErrors(next);
        if (next.name || next.email || next.sells || next.when || !at) return;
        setBusy(true);
        setFailure(null);
        try {
            const proof = signedIn ? {} : await human.waitForProof();
            const trip = journey ?? readJourney();
            const opt = (v: string) => v.trim() || undefined;
            const res = await api.requestDemo(
                {
                    name: name.trim(),
                    email: email.trim(),
                    phone: opt(phone),
                    company: opt(company),
                    website: opt(website),
                    sells: sells as DemoSells,
                    reason,
                    preferredAt: at.toISOString(),
                    ...(zone ? { timeZone: zone } : {}),
                    message: opt(message),
                    locale,
                    company_url: companyUrl,
                    startedAt,
                    ...(trip.journeyId ? { journeyId: trip.journeyId } : {}),
                    ...(trip.source ? { source: trip.source } : {}),
                    ...(demoSlug ? { demoSlug } : {}),
                    ...proof,
                },
                signedIn,
            );
            setSent({ bookingUrl: res?.bookingUrl || configRef.current?.demoBookingUrl || null, when: formatSlot(at, locale), email: email.trim() });
            onSent?.();
        } catch (e) {
            const code = await readSignupError(e);
            setFailure(code);
            // Someone took that time first: everything typed stays, only the time is asked again.
            if (code === 'DEMO_SLOT_TAKEN') {
                setTime('');
                refreshBusy();
            }
            // Every proof is single-use: the next attempt needs a new one.
            if (!signedIn) human.renew();
        } finally {
            setBusy(false);
        }
    };

    if (sent) {
        return (
            <div className="flex flex-col items-center gap-3 px-2 pt-6 pb-2 text-center" data-testid="book-demo-sent" role="status">
                <span className="flex size-11 items-center justify-center rounded-full border-[1.5px] border-success text-success">
                    <Check className="size-5" />
                </span>
                <DialogTitle className="font-heading text-lg font-medium">{t('SIGNUP.DEMO.SENT_TITLE')}</DialogTitle>
                <DialogDescription className="max-w-[380px] leading-relaxed">{t('SIGNUP.DEMO.SENT_BODY_AT', { when: sent.when, email: sent.email })}</DialogDescription>
                <div className="mt-3 flex flex-wrap justify-center gap-2">
                    {sent.bookingUrl && (
                        <Button variant="outline" asChild>
                            <a href={sent.bookingUrl} target="_blank" rel="noopener noreferrer" data-testid="book-demo-pick-time">
                                <CalendarClock data-icon="inline-start" />
                                {t('SIGNUP.DEMO.PICK_TIME')}
                            </a>
                        </Button>
                    )}
                    <Button variant="ghost" onClick={onClose} data-testid="book-demo-close">
                        {t('SIGNUP.DEMO.CLOSE')}
                    </Button>
                </div>
            </div>
        );
    }

    const field = (id: string, label: string, input: React.ReactNode, error?: string | false) => (
        <div className="grid gap-1.5">
            <Label htmlFor={id} className="text-xs font-medium text-muted-foreground">
                {label}
            </Label>
            {input}
            {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
    );

    return (
        <form
            noValidate
            aria-busy={busy}
            onSubmit={(e) => {
                e.preventDefault();
                submit();
            }}
            className="grid gap-4"
            data-testid="book-demo-form"
        >
            <DialogHeader className="pe-6">
                <DialogTitle className="font-heading text-lg font-medium">{t('SIGNUP.DEMO.TITLE')}</DialogTitle>
                <DialogDescription className="leading-relaxed">{t('SIGNUP.DEMO.SUBTITLE')}</DialogDescription>
            </DialogHeader>

            <fieldset disabled={busy} className="contents">
                <div className="grid gap-4 sm:grid-cols-2">
                    {field(
                        'demo-name',
                        t('SIGNUP.DEMO.NAME'),
                        <Input id="demo-name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} aria-invalid={errors.name || undefined} />,
                        errors.name && t('SIGNUP.DEMO.NAME_REQUIRED'),
                    )}
                    {field(
                        'demo-email',
                        t('SIGNUP.DEMO.EMAIL'),
                        <Input id="demo-email" type="email" dir="ltr" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={errors.email || undefined} />,
                        errors.email && t('SIGNUP.DEMO.EMAIL_INVALID'),
                    )}
                    {field('demo-phone', t('SIGNUP.DEMO.PHONE'), <Input id="demo-phone" type="tel" dir="ltr" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />)}
                    {field('demo-company', t('SIGNUP.DEMO.COMPANY'), <Input id="demo-company" autoComplete="organization" value={company} onChange={(e) => setCompany(e.target.value)} />)}
                </div>
                {field(
                    'demo-website',
                    t('SIGNUP.DEMO.WEBSITE'),
                    <Input id="demo-website" dir="ltr" autoComplete="url" placeholder={t('SIGNUP.DEMO.WEBSITE_PLACEHOLDER') as string} value={website} onChange={(e) => setWebsite(e.target.value)} />,
                )}

                <div className="grid gap-1.5" role="group" aria-labelledby="demo-sells-label">
                    <span id="demo-sells-label" className="text-xs font-medium text-muted-foreground">
                        {t('SIGNUP.DEMO.SELLS')}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                        {DEMO_SELLS.map((s) => (
                            <FilterChip key={s} size="md" label={t(`SIGNUP.DEMO.SELLS_${s.toUpperCase()}`)} selected={sells === s} onClick={() => setSells(s)} />
                        ))}
                    </div>
                    {errors.sells && sells === null && <p className="text-xs text-destructive">{t('SIGNUP.DEMO.SELLS_REQUIRED')}</p>}
                </div>

                <div className="grid gap-4">
                    {field(
                        'demo-reason',
                        t('SIGNUP.DEMO.REASON'),
                        <Select value={reason} onValueChange={(v) => setReason(v as DemoReason)}>
                            <SelectTrigger id="demo-reason" className="w-full" aria-label={t('SIGNUP.DEMO.REASON') as string}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="z-[10001]">
                                {DEMO_REASONS.map((r) => (
                                    <SelectItem key={r} value={r}>
                                        {t(`SIGNUP.DEMO.REASONS.${r.toUpperCase()}`)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>,
                    )}
                </div>

                {/* The call: a weekday and a half-hour slot, in their own time zone. */}
                <div className="grid gap-1.5" role="group" aria-labelledby="demo-when-label">
                    <span id="demo-when-label" className="text-xs font-medium text-muted-foreground">
                        {t('SIGNUP.DEMO.WHEN')}
                    </span>
                    <div className="grid gap-2 sm:grid-cols-2">
                        <Input
                            id="demo-day"
                            type="date"
                            min={firstDay()}
                            max={lastDay()}
                            value={day}
                            onChange={(e) => {
                                setDay(e.target.value);
                                setTime('');
                            }}
                            aria-label={t('SIGNUP.DEMO.DAY') as string}
                            aria-invalid={(errors.when && !day) || undefined}
                            data-testid="book-demo-day"
                        />
                        <Select value={time} onValueChange={setTime} onOpenChange={(opening) => opening && refreshBusy()} disabled={!slots.length}>
                            <SelectTrigger id="demo-slot" className="w-full" aria-label={t('SIGNUP.DEMO.SLOT') as string} data-testid="book-demo-slot">
                                <SelectValue placeholder={t(day && !slots.length ? 'SIGNUP.DEMO.NO_SLOTS' : 'SIGNUP.DEMO.SLOT_PLACEHOLDER') as string} />
                            </SelectTrigger>
                            <SelectContent className="z-[10001] max-h-64">
                                {slots.map((s) => (
                                    <SelectItem key={s} value={s}>
                                        {s}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {errors.when ? (
                        <p className="text-xs text-destructive">{t(day && !isWeekday(day) ? 'SIGNUP.DEMO.WEEKDAY_ONLY' : 'SIGNUP.DEMO.WHEN_REQUIRED')}</p>
                    ) : (
                        <p className={dayFull ? 'text-xs text-foreground' : 'text-xs text-muted-foreground'} data-testid={dayFull ? 'book-demo-day-full' : undefined}>
                            {day && !isWeekday(day)
                                ? t('SIGNUP.DEMO.WEEKDAY_ONLY')
                                : dayFull
                                  ? t('SIGNUP.DEMO.DAY_FULL')
                                  : t('SIGNUP.DEMO.WHEN_HINT', { zone: zone || 'UTC' })}
                        </p>
                    )}
                </div>
                {field(
                    'demo-message',
                    t('SIGNUP.DEMO.MESSAGE'),
                    <Textarea id="demo-message" rows={3} placeholder={t('SIGNUP.DEMO.MESSAGE_PLACEHOLDER') as string} value={message} onChange={(e) => setMessage(e.target.value)} />,
                )}

                {/* Off-screen rather than display:none, which some bots skip. */}
                <div aria-hidden="true" className="pointer-events-none absolute -start-[10000px] top-auto size-px overflow-hidden">
                    <label>
                        Company URL
                        <input type="text" name="company_url" tabIndex={-1} autoComplete="off" value={companyUrl} onChange={(e) => setCompanyUrl(e.target.value)} data-testid="book-demo-honeypot" />
                    </label>
                </div>

                {!signedIn && human.mode === 'turnstile' && config?.turnstileSiteKey && (
                    <Turnstile
                        siteKey={config.turnstileSiteKey}
                        language={locale}
                        onToken={human.onTurnstileToken}
                        onError={human.onTurnstileError}
                        resetRef={human.resetTurnstile}
                        className="flex justify-center"
                    />
                )}

                {failure && (
                    <p role="alert" className="text-sm leading-relaxed text-destructive" data-testid={`book-demo-error-${failure}`}>
                        {t(errorKey(failure))}
                    </p>
                )}

                <DialogFooter className="mt-1">
                    <Button type="button" variant="ghost" onClick={onClose}>
                        {t('SIGNUP.DEMO.CANCEL')}
                    </Button>
                    <Button type="submit" variant="outline" data-testid="book-demo-submit">
                        {busy && <Spinner />}
                        {t('SIGNUP.DEMO.SUBMIT')}
                    </Button>
                </DialogFooter>
            </fieldset>
        </form>
    );
};

/**
 * The one line that opens the dialog: a sentence and the link in it. Used by
 * the website step, the connect step, the review screen and the blocked one.
 */
export const BookDemoPrompt = ({ text, onClick, className, testId = 'book-demo-open' }: { text: string; onClick: () => void; className?: string; testId?: string }) => {
    const { t } = useTranslation();
    return (
        <p className={className ?? 'text-sm leading-relaxed text-muted-foreground'}>
            {text}{' '}
            <button type="button" onClick={onClick} className="font-medium text-foreground underline-offset-4 hover:underline" data-testid={testId}>
                {t('SIGNUP.DEMO.OPEN')}
            </button>
        </p>
    );
};
